import { supabase } from '@/core/supabase/client';

/**
 * Cómo se repartió cada trabajo del mes, sin esperar a cerrarlo.
 *
 * El resumen por persona contesta cuánto ganó cada uno. Cuando ese número no
 * coincide con la planilla que se lleva a mano, no dice DÓNDE está la
 * diferencia: hay que abrir los trabajos de a uno y rehacer la cuenta.
 *
 * Esto lo contesta. Y la cuenta es la misma que la de la liquidación —la
 * función del servidor repite las expresiones de `app.compute_settlement` sin
 * agrupar—, así que el detalle suma exactamente el total. Si no sumara, no
 * serviría para reconciliar nada.
 */

export interface RepartoDeUno {
  participant_id: string;
  display_name: string;
  honoraria_cents: number;
  mobility_cents: number;
}

export interface TrabajoRepartido {
  work_session_id: string;
  work_date: string;
  client_name: string;
  establishment_name: string | null;
  service_names: string | null;
  honoraria_cents: number | null;
  mobility_cents: number | null;
  total_cents: number | null;
  total_kg: number;
  is_billed: boolean;
  is_paid: boolean;
  /** A quién se le imputa la facturación: quien cobra, quien factura, o la organización. */
  billed_to_participant_id: string | null;
  reparto: RepartoDeUno[];
}

export interface RepartoDelMes {
  organization_id: string;
  year: number;
  month: number;
  /**
   * Cuántos ajustes tiene el período.
   *
   * Los ajustes NO viven en ningún trabajo: son asientos sueltos del mes. Por
   * eso la suma de este detalle es lo ganado ANTES de ajustes, y la pantalla lo
   * dice en vez de dejar que la diferencia se descubra restando.
   */
  adjustments_count: number;
  works: TrabajoRepartido[];
}

export async function fetchRepartoDelMes(
  organizationId: string,
  year: number,
  month: number,
): Promise<RepartoDelMes> {
  const { data, error } = await supabase.rpc('monthly_distribution', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return data as RepartoDelMes;
}

/** Lo que le tocó a una persona en un trabajo: honorarios más movilidad. */
export const tocaEnElTrabajo = (r: RepartoDeUno) => r.honoraria_cents + r.mobility_cents;

/**
 * El total del mes por persona, sumando el detalle.
 *
 * Se calcula acá y no se pide al servidor a propósito: es el mismo número que
 * ya devuelve el resumen, y verlos coincidir es justamente la comprobación que
 * esta pantalla viene a permitir.
 */
export function totalesPorPersona(
  d: RepartoDelMes,
): { participant_id: string; display_name: string; cents: number }[] {
  const por = new Map<string, { participant_id: string; display_name: string; cents: number }>();
  for (const w of d.works) {
    for (const r of w.reparto) {
      const actual = por.get(r.participant_id) ?? {
        participant_id: r.participant_id,
        display_name: r.display_name,
        cents: 0,
      };
      actual.cents += tocaEnElTrabajo(r);
      por.set(r.participant_id, actual);
    }
  }
  return [...por.values()].sort((a, b) => a.display_name.localeCompare(b.display_name, 'es'));
}

// ---------------------------------------------------------------------------
// Cuánto le toca a cada uno, cliente por cliente
// ---------------------------------------------------------------------------

export interface ParteDeUnCliente {
  client_name: string;
  /** Lo que le toca a cada persona, por `participant_id`. */
  porPersona: Map<string, number>;
}

export interface RepartoPorCliente {
  /** Las personas del mes, en el orden en que van las columnas. */
  personas: { participant_id: string; display_name: string }[];
  clientes: ParteDeUnCliente[];
}

/**
 * Cliente por cliente, cuánto es de cada uno.
 *
 * A la veterinaria le llega la plata de un cliente y ahí mismo hay que
 * separarla. Quien lo hace no usa la aplicación: mira una hoja, busca el
 * cliente y entrega. Por eso esto devuelve una cosa sola —nombre y montos— y
 * nada más: cada columna de más es una que hay que explicarle.
 *
 * La movilidad ya viene adentro de lo de cada uno. Si el viaje lo puso Felo, a
 * Felo le toca su parte del trabajo más la movilidad entera, y eso es un solo
 * número.
 *
 * Se arma sumando el mismo detalle que ya muestra la aplicación, sin pedirle
 * nada nuevo al servidor: por eso suma exactamente lo que dice la liquidación.
 * Un trabajo sin precio todavía no reparte nada y no entra.
 */
export function repartoPorCliente(d: RepartoDelMes): RepartoPorCliente {
  const personas = new Map<string, string>();
  const clientes = new Map<string, ParteDeUnCliente>();

  for (const w of d.works) {
    const cliente = clientes.get(w.client_name) ?? {
      client_name: w.client_name,
      porPersona: new Map<string, number>(),
    };
    for (const r of w.reparto) {
      personas.set(r.participant_id, r.display_name);
      if (w.total_cents === null) continue;
      cliente.porPersona.set(
        r.participant_id,
        (cliente.porPersona.get(r.participant_id) ?? 0) + tocaEnElTrabajo(r),
      );
    }
    clientes.set(w.client_name, cliente);
  }

  return {
    personas: [...personas]
      .map(([participant_id, display_name]) => ({ participant_id, display_name }))
      .sort((a, b) => a.display_name.localeCompare(b.display_name, 'es')),
    clientes: [...clientes.values()].sort((a, b) =>
      a.client_name.localeCompare(b.client_name, 'es'),
    ),
  };
}
